import { useState } from "react";
import { cn, getInitials } from "@/lib/utils";
import { resolveFileUrl } from "@/lib/constants";

interface AvatarProps {
  src?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}

const sizeClasses = {
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-base",
  xl: "h-16 w-16 text-lg",
};

export function Avatar({
  src,
  firstName,
  lastName,
  size = "md",
  className,
}: AvatarProps) {
  const initials = getInitials(firstName, lastName);
  // Resolve relative API paths (legacy ``/api/v1/files/<key>`` rows
  // saved before the backend started emitting absolute URLs) by
  // prepending the SPA's known API origin. Absolute URLs pass
  // through untouched.
  const resolvedSrc = resolveFileUrl(src);
  // Track image-load failures so a broken URL falls back to initials
  // instead of showing the OS-default broken-image icon. ``key``-ed
  // on the URL so a fresh upload after a previous failure tries again.
  const [failed, setFailed] = useState(false);

  if (resolvedSrc && !failed) {
    return (
      <img
        key={resolvedSrc}
        src={resolvedSrc}
        alt={`${firstName || ""} ${lastName || ""}`}
        onError={() => setFailed(true)}
        className={cn(
          "rounded-full object-cover",
          sizeClasses[size],
          className
        )}
      />
    );
  }

  return (
    <div
      className={cn(
        "rounded-full bg-accent/10 text-accent flex items-center justify-center font-medium",
        sizeClasses[size],
        className
      )}
    >
      {initials || "?"}
    </div>
  );
}
